import { Component, OnDestroy, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from "@angular/forms"
import { RouterModule, Router } from "@angular/router"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatSelectModule } from "@angular/material/select"
import { SnackbarService } from "../../services/snackbar.service"
import { StationClock } from "../../models/station-clock.model"
import { Station } from "../../models/station.model"
import { StationClockService } from "../../services/station-clock.service"
import { StationStoreService } from "../../services/station-store.service"
import { StationHeaderComponent } from "../../shared/station-header/station-header.component"
import {
  CommonTableComponent,
  TableColumn,
  TableAction,
} from "../../shared/common-table/common-table.component"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { PermissionService } from "../../services/permission.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { canManageStationConfig, normalizeStationRole } from "../../utils/station-role.util"
import { NoScrollInputDirective } from "../../directives/no-scroll-input.directive"
import { Subject, takeUntil } from "rxjs"
import { DAY_OF_WEEK_OPTIONS, dayOfWeekLabel, parseDayOfWeek } from "../../utils/day-of-week"

@Component({
  selector: "app-manage-station-clocks",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    StationHeaderComponent,
    CommonTableComponent,
    PageHeaderComponent,
    NoScrollInputDirective,
  ],
  templateUrl: "./manage-station-clocks.component.html",
  styleUrls: ["./manage-station-clocks.component.css"],
})
export class ManageStationClocksComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  stationClocks: StationClock[] = []
  stations: Station[] = []
  selectedStationId: number | null = null
  selectedStation: Station | null = null

  // Pagination properties
  currentPage = 1
  pageSize = 10
  totalCount = 0
  totalPages = 1

  canEdit = false
  canDelete = false
  canAdd = false
  isViewOnly = false

  tableColumns: TableColumn[] = [
    { key: "day_of_week", label: "Day", width: "26%", class: "th-day" },
    { key: "break_start_time", label: "Break Start Time", width: "30%", class: "th-time" },
    { key: "break_length", label: "Break Length (min)", width: "10rem", class: "th-length" },
  ]

  tableActions: TableAction[] = [
    {
      icon: "edit",
      label: "Edit clock",
      class: "btn-edit",
      callback: (clock: StationClock) => this.openEditDialog(clock),
      show: () => this.canEdit,
    },
    {
      icon: "delete",
      label: "Delete clock",
      class: "btn-delete",
      callback: (clock: StationClock) => {
        const id = this.getClockId(clock)
        if (id != null) this.deleteClock(id)
      },
      show: () => this.canDelete,
    },
  ]

  stationLoading = false
  stationClockLoading = false
  dialogSubmitting = false
  isDialogOpen = false
  isEditMode = false
  selectedClock: StationClock | null = null
  clockForm: FormGroup

  timeGrid: boolean[][] = []
  hours: string[] = []
  days: { value: number; label: string }[] = []

  // Grid row index == day_of_week (0=Sunday), see utils/day-of-week.ts.
  dayOptions = [...DAY_OF_WEEK_OPTIONS]

  // Day-of-week filter ("" = All Days, restores pagination)
  selectedDayFilter: number | "" = ""

  // Bulk selection (only used when a specific day is filtered)
  selectedClockIds = new Set<number>()
  showMassChangeForm = false
  massChangeMinute: number | null = null
  bulkSubmitting = false

  // Grid-style Bulk Delete dialog
  isBulkDeleteOpen = false
  bulkDeleteStationId: number | null = null
  /** Bumped per list request so only the latest (station/page/day) response is applied. */
  private clocksRequestSeq = 0
  bulkDeleteGrid: boolean[][] = []
  bulkDeleteLoading = false
  bulkDeleteSubmitting = false

  constructor(
    private stationClockService: StationClockService,
    private stationStore: StationStoreService,
    private formBuilder: FormBuilder,
    private snackbar: SnackbarService,
    private router: Router,
    private permissionService: PermissionService,
    private confirmDialog: ConfirmDialogService,
  ) {
    this.initializeTimeGrid()
    this.clockForm = this.formBuilder.group({
      stationid: ["", Validators.required],
      break_length: ["", [Validators.required, Validators.min(1)]],
      break_start_minute: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
    })
  }

  ngOnInit() {
    this.permissionService.currentRole$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyPermissionsForSelectedStation()
    })
    this.loadStations()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private applyPermissionsForSelectedStation(station?: Station | null): void {
    const target = station ?? this.selectedStation
    const role = normalizeStationRole(target?.user_role ?? this.permissionService.getCurrentRole())
    const canManage = canManageStationConfig(role)
    this.canEdit = canManage
    this.canDelete = canManage
    this.canAdd = canManage
    this.isViewOnly = !canManage && ["station_user", "user"].includes(role)
  }

  loadStations() {
    this.stationLoading = this.stations.length === 0
    this.stationStore.watchStations().pipe(takeUntil(this.destroy$)).subscribe({
      next: (stations) => {
        this.stations = stations
        this.stationLoading = false
        if (this.stations.length && !this.selectedStationId) {
          this.selectedStationId = this.stations[0].id
          this.selectedStation = this.stations[0]
          this.clockForm.get("stationid")?.setValue(this.selectedStationId)
          this.applyPermissionsForSelectedStation(this.stations[0])
          this.loadStationClocks()
        }
      },
      error: (error) => {
        console.error("Error loading stations:", error)
        this.showSnackBar("Error loading stations", "error")
        this.stationLoading = false
      },
    })
  }

  onStationChange(station: Station) {
    this.selectedStation = station
    this.selectedStationId = station.id
    this.clockForm.get("stationid")?.setValue(this.selectedStationId)
    this.applyPermissionsForSelectedStation(station)
    // Page/selection belong to the previous station; its page N may not exist here.
    this.currentPage = 1
    this.clearSelection()
    this.loadStationClocks()
  }

  refreshData(): void {
    if (!this.selectedStationId) return
    this.loadStationClocks()
  }

  loadStationClocks() {
    if (!this.selectedStationId) return;
    this.stationClockLoading = true;
    this.clearSelection();

    const daySpecific = this.isDaySpecific;
    const dayParam = daySpecific ? Number(this.selectedDayFilter) : null;
    // Day-specific view is not paginated (backend returns all clocks for the day).
    const page = daySpecific ? 1 : this.currentPage;
    const limit = daySpecific ? 1000 : this.pageSize;
    const requestSeq = ++this.clocksRequestSeq;

    this.stationClockService
      .getStationClocksByStation(this.selectedStationId, page, limit, dayParam)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // A newer request (station switch, page or day change) superseded this one.
          if (requestSeq !== this.clocksRequestSeq) return;
          this.stationClocks = response.data.map((clock) => ({
            ...clock,
            dayName: this.getDayName(clock.day_of_week),
          }));

          if (daySpecific) {
            this.totalCount = this.stationClocks.length;
            this.currentPage = 1;
            this.totalPages = 1;
          } else {
            this.totalCount = response.totalCount;
            this.currentPage = response.currentPage;
            this.pageSize = response.pageSize;
            this.totalPages = response.totalPages;
          }

          this.stationClockLoading = false;
        },
        error: (error) => {
          if (requestSeq !== this.clocksRequestSeq) return;
          console.error("Error loading station clocks:", error);
          this.showSnackBar("Error loading station clocks", "error");
          this.stationClockLoading = false;
        },
      });
  }

  // ============================================================
  // Fix 1 — Day of Week filter
  // ============================================================
  get isDaySpecific(): boolean {
    return this.selectedDayFilter !== ""
  }

  onDayFilterChange() {
    this.currentPage = 1
    this.clearSelection()
    this.loadStationClocks()
  }

  // ============================================================
  // Fix 2 — Row selection + Select All (day-specific view only)
  // ============================================================
  /** Resolve the primary key from a clock row (API returns `id`). */
  getClockId(clock: StationClock | Record<string, unknown> | null | undefined): number | null {
    if (!clock || typeof clock !== "object") return null
    const rawObj = clock as unknown as Record<string, unknown>
    const raw =
      (clock as StationClock).id ??
      rawObj["clock_id"] ??
      rawObj["stationClockId"] ??
      rawObj["station_clock_id"]
    const id = typeof raw === "string" ? Number(raw) : Number(raw)
    return Number.isFinite(id) && id > 0 ? id : null
  }

  isClockSelected(clock: StationClock): boolean {
    const id = this.getClockId(clock)
    return id != null && this.selectedClockIds.has(id)
  }

  toggleClockSelection(clock: StationClock): void {
    const id = this.getClockId(clock)
    if (id == null) return
    const next = new Set(this.selectedClockIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    this.selectedClockIds = next
  }

  get allVisibleSelected(): boolean {
    return (
      this.stationClocks.length > 0 &&
      this.stationClocks.every((c) => {
        const id = this.getClockId(c)
        return id != null && this.selectedClockIds.has(id)
      })
    )
  }

  get someVisibleSelected(): boolean {
    return this.selectedClockIds.size > 0 && !this.allVisibleSelected
  }

  toggleSelectAll(): void {
    if (this.allVisibleSelected) {
      this.selectedClockIds = new Set()
      return
    }
    const next = new Set(this.selectedClockIds)
    this.stationClocks.forEach((c) => {
      const id = this.getClockId(c)
      if (id != null) next.add(id)
    })
    this.selectedClockIds = next
  }

  private clearSelection(): void {
    this.selectedClockIds = new Set()
    this.showMassChangeForm = false
    this.massChangeMinute = null
  }

  // ============================================================
  // Fix 3 — Mass Change + Delete Selected
  // ============================================================
  openMassChange() {
    this.showMassChangeForm = true
    this.massChangeMinute = null
  }

  cancelMassChange() {
    this.showMassChangeForm = false
    this.massChangeMinute = null
  }

  applyMassChange() {
    const minute = Number(this.massChangeMinute)
    if (this.massChangeMinute === null || !Number.isFinite(minute) || minute < 0 || minute > 59) {
      this.showSnackBar("Enter a break minute between 0 and 59", "warning")
      return
    }
    const clockIds = Array.from(this.selectedClockIds).filter((id) => Number.isFinite(id) && id > 0)
    if (!clockIds.length) {
      this.showSnackBar("No clock IDs selected", "warning")
      return
    }

    this.bulkSubmitting = true
    this.stationClockService.bulkUpdateBreakMinute(clockIds, minute).subscribe({
      next: () => {
        this.showSnackBar(
          `Updated break minute for ${clockIds.length} clock${clockIds.length > 1 ? "s" : ""}`,
          "success",
        )
        this.bulkSubmitting = false
        this.clearSelection()
        this.loadStationClocks()
      },
      error: (error) => {
        console.error("Error updating clocks:", error)
        this.showSnackBar(this.snackbar.apiError(error, "Error updating clocks"), "error")
        this.bulkSubmitting = false
      },
    })
  }

  deleteSelected() {
    const clockIds = Array.from(this.selectedClockIds).filter((id) => Number.isFinite(id) && id > 0)
    if (!clockIds.length) {
      this.showSnackBar("No clock IDs selected", "warning")
      return
    }

    this.confirmDialog
      .open({
        title: "Delete selected clocks",
        message: `Delete ${clockIds.length} selected clock${clockIds.length > 1 ? "s" : ""}? This cannot be undone.`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.bulkSubmitting = true
        this.stationClockService.bulkDeleteClocks(clockIds).subscribe({
          next: () => {
            this.showSnackBar(
              `Deleted ${clockIds.length} clock${clockIds.length > 1 ? "s" : ""}`,
              "success",
            )
            this.bulkSubmitting = false
            this.clearSelection()
            this.loadStationClocks()
          },
          error: (error) => {
            console.error("Error deleting clocks:", error)
            this.showSnackBar(this.snackbar.apiError(error, "Error deleting clocks"), "error")
            this.bulkSubmitting = false
          },
        })
      })
  }

  // ============================================================
  // Fix 4 — Grid-style Bulk Delete dialog
  // ============================================================
  openBulkDeleteDialog() {
    if (!this.canDelete) {
      this.showSnackBar("You don't have permission to delete clocks", "warning")
      return
    }
    if (!this.selectedStationId) return
    this.bulkDeleteStationId = this.selectedStationId
    this.resetBulkDeleteGrid()
    this.isBulkDeleteOpen = true
    this.loadExistingClocksIntoBulkGrid(this.bulkDeleteStationId)
  }

  closeBulkDeleteDialog() {
    this.isBulkDeleteOpen = false
    this.resetBulkDeleteGrid()
  }

  onBulkDeleteStationChange() {
    if (this.bulkDeleteStationId) {
      this.loadExistingClocksIntoBulkGrid(this.bulkDeleteStationId)
    }
  }

  private resetBulkDeleteGrid() {
    this.bulkDeleteGrid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => false))
  }

  /** Parse API day_of_week (0-6 or day name) to grid row index 0=Sunday … 6=Saturday. */
  private parseDayOfWeekIndex(raw: unknown): number | null {
    return parseDayOfWeek(raw)
  }

  /** Parse HH:MM[:SS] (or Date/timedelta string) to hour column index 0-23. */
  private parseHourIndex(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === "") return null
    const text = String(raw).trim()
    // Common API shape: "12:18:00" or "12:18"
    const match = text.match(/(\d{1,2})\s*:/)
    if (match) {
      const hour = Number.parseInt(match[1], 10)
      return Number.isFinite(hour) && hour >= 0 && hour < 24 ? hour : null
    }
    const asNumber = Number.parseInt(text, 10)
    return Number.isFinite(asNumber) && asNumber >= 0 && asNumber < 24 ? asNumber : null
  }

  private applyClocksToBulkDeleteGrid(clocks: StationClock[], stationId: number): void {
    this.resetBulkDeleteGrid()
    // Assign a new grid reference after marking so Angular change detection always updates the UI.
    const nextGrid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => false))

    for (const clock of clocks) {
      const raw = clock as unknown as Record<string, unknown>
      const clockStationId = Number(clock.stationid ?? raw["station_id"])
      if (Number.isFinite(clockStationId) && clockStationId !== Number(stationId)) {
        continue
      }

      const dayIndex = this.parseDayOfWeekIndex(clock.day_of_week ?? raw["day"])
      const hourIndex = this.parseHourIndex(clock.break_start_time ?? raw["breakStartTime"])
      if (dayIndex == null || hourIndex == null) continue
      nextGrid[dayIndex][hourIndex] = true
    }

    this.bulkDeleteGrid = nextGrid
  }

  private loadExistingClocksIntoBulkGrid(stationId: number) {
    this.bulkDeleteLoading = true
    this.resetBulkDeleteGrid()
    // Walk all paginated pages — backend max limit is 100, so one call can truncate later days.
    this.stationClockService.getAllStationClocksForStation(stationId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (clocks) => {
        // Station changed in the dialog while this loaded: the grid would show (and then
        // submit) station A's clocks as the selection for station B.
        if (this.bulkDeleteStationId !== stationId) return
        this.applyClocksToBulkDeleteGrid(clocks, stationId)
        this.bulkDeleteLoading = false
      },
      error: (error) => {
        if (this.bulkDeleteStationId !== stationId) return
        console.error("Error loading existing clocks for bulk delete:", error)
        this.showSnackBar("Error loading existing clocks", "error")
        this.bulkDeleteLoading = false
      },
    })
  }

  onBulkDeleteGridChange(dayIndex: number, hourIndex: number) {
    this.bulkDeleteGrid[dayIndex][hourIndex] = !this.bulkDeleteGrid[dayIndex][hourIndex]
  }

  onBulkDeleteHourHeaderClick(hourIndex: number) {
    const allSelected = this.bulkDeleteGrid.every((day) => day[hourIndex])
    this.bulkDeleteGrid.forEach((day) => {
      day[hourIndex] = !allSelected
    })
  }

  onBulkDeleteDayHeaderClick(dayIndex: number) {
    const allSelected = this.bulkDeleteGrid[dayIndex].every((hour) => hour)
    this.bulkDeleteGrid[dayIndex] = this.bulkDeleteGrid[dayIndex].map(() => !allSelected)
  }

  getBulkDeleteSelections(): { day: number; hour: number }[] {
    const selections: { day: number; hour: number }[] = []
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      for (let hourIndex = 0; hourIndex < 24; hourIndex++) {
        if (this.bulkDeleteGrid[dayIndex][hourIndex]) {
          selections.push({ day: dayIndex, hour: hourIndex })
        }
      }
    }
    return selections
  }

  get bulkDeleteSelectionCount(): number {
    return this.getBulkDeleteSelections().length
  }

  submitBulkDelete() {
    const selections = this.getBulkDeleteSelections()
    if (!selections.length) {
      this.showSnackBar("Select at least one day/hour combination to delete", "warning")
      return
    }
    if (!this.bulkDeleteStationId || this.bulkDeleteLoading) return

    this.bulkDeleteSubmitting = true
    this.stationClockService.bulkDeleteClocksGrid(this.bulkDeleteStationId, selections).subscribe({
      next: () => {
        this.showSnackBar(
          `Deleted ${selections.length} clock${selections.length > 1 ? "s" : ""}`,
          "success",
        )
        this.bulkDeleteSubmitting = false
        this.closeBulkDeleteDialog()
        this.loadStationClocks()
      },
      error: (error) => {
        console.error("Error during grid bulk delete:", error)
        this.showSnackBar(this.snackbar.apiError(error, "Error deleting clocks"), "error")
        this.bulkDeleteSubmitting = false
      },
    })
  }

  private initializeTimeGrid() {
    this.hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0") + ":00")
    this.days = [...this.dayOptions]
    this.timeGrid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => false))
  }

  openAddDialog() {
    if (!this.canAdd) {
      this.showSnackBar("You don't have permission to add clocks", "warning")
      return
    }
    this.isEditMode = false
    this.selectedClock = null
    this.clockForm.reset()
    if (this.selectedStationId) this.clockForm.get("stationid")?.setValue(this.selectedStationId)
    this.resetTimeGrid()
    this.isDialogOpen = true
  }

  openEditDialog(clock: StationClock) {
    if (!this.canEdit) {
      this.showSnackBar("You don't have permission to edit clocks", "warning")
      return
    }
    this.isEditMode = true
    this.selectedClock = clock

    // Extract minute from break_start_time
    let minute = 0
    if (clock.break_start_time) {
      const parts = clock.break_start_time.split(":")
      if (parts.length >= 2) {
        minute = Number(parts[1])
      }
    }

    // Extract hour from break_start_time
    let hour = 0
    if (clock.break_start_time) {
      const parts = clock.break_start_time.split(":")
      if (parts.length >= 1) {
        hour = Number(parts[0])
      }
    }

    this.clockForm.patchValue({
      stationid: clock.stationid,
      break_length: clock.break_length,
      break_start_minute: minute,
    })

    // Set the time grid to show the current clock's time
    this.resetTimeGrid()
    const dayIndex = this.parseDayOfWeekIndex(clock.day_of_week)
    if (dayIndex != null && hour >= 0 && hour < 24) {
      this.timeGrid[dayIndex][hour] = true
    }

    this.isDialogOpen = true
  }

  closeDialog() {
    this.isDialogOpen = false
    this.clockForm.reset()
    this.selectedClock = null
    this.isEditMode = false
    this.resetTimeGrid()
  }

  private resetTimeGrid() {
    this.timeGrid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => false))
  }

  private loadExistingClocksForStation(stationId: number) {
    this.stationClockService.getAllStationClocksForStation(stationId).subscribe({
      next: (clocks) => {
        this.resetTimeGrid()
        clocks.forEach((clock: StationClock) => {
          const dayIndex = this.parseDayOfWeekIndex(clock.day_of_week)
          const hourIndex = this.parseHourIndex(clock.break_start_time)
          if (dayIndex == null || hourIndex == null) return
          const clockStationId = Number(clock.stationid)
          if (Number.isFinite(clockStationId) && clockStationId !== Number(stationId)) return
          this.timeGrid[dayIndex][hourIndex] = true
        })
      },
      error: (error) => {
        console.error("Error loading existing clocks for station:", error)
      },
    })
  }

  onTimeGridChange(dayIndex: number, hourIndex: number) {
    this.timeGrid[dayIndex][hourIndex] = !this.timeGrid[dayIndex][hourIndex]
  }

  onHourHeaderClick(hourIndex: number) {
    const allSelected = this.timeGrid.every((day) => day[hourIndex])
    this.timeGrid.forEach((day) => {
      day[hourIndex] = !allSelected
    })
  }

  onDayHeaderClick(dayIndex: number) {
    const allSelected = this.timeGrid[dayIndex].every((hour) => hour)
    this.timeGrid[dayIndex] = this.timeGrid[dayIndex].map(() => !allSelected)
  }

  getSelectedTimes(): { day: number; time: string }[] {
    const selectedTimes: { day: number; time: string }[] = []

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      for (let hourIndex = 0; hourIndex < 24; hourIndex++) {
        if (this.timeGrid[dayIndex][hourIndex]) {
          selectedTimes.push({
            day: dayIndex,
            time: this.hours[hourIndex],
          })
        }
      }
    }

    return selectedTimes
  }

  onSubmit() {
    if (this.clockForm.valid) {
      const selectedTimes = this.getSelectedTimes()

      if (selectedTimes.length === 0) {
        this.showSnackBar("Please select at least one break time", "error")
        return
      }
      const formValue = this.clockForm.value
      this.dialogSubmitting = true

      if (this.isEditMode && this.selectedClock) {
        // Edit mode: update the single clock entry
        if (selectedTimes.length > 1) {
          this.showSnackBar("Please select only one time slot when editing", "error")
          this.dialogSubmitting = false
          return
        }

        const selectedTime = selectedTimes[0]
        const updateData = {
          stationid: formValue.stationid,
          break_length: Number(formValue.break_length),
          day_of_week: selectedTime.day,
          break_start_time: `${selectedTime.time.split(':')[0]}:${formValue.break_start_minute.toString().padStart(2, '0')}:00`,
        }

        this.stationClockService.updateStationClock(this.selectedClock.id!, updateData).subscribe({
          next: () => {
            this.showSnackBar("Station clock updated successfully", "success")
            this.dialogSubmitting = false
            this.closeDialog()
            this.loadStationClocks()
          },
          error: (error) => {
            console.error("Error updating station clock:", error)
            this.showSnackBar("Error updating station clock", "error")
            this.dialogSubmitting = false
          },
        })
      } else {
        // Add mode: create new clocks
        this.createNewClocks(formValue, selectedTimes)
      }
    }
  }

  deleteClock(id: number) {
    if (!this.canDelete) {
      this.showSnackBar("You don't have permission to delete clocks", "warning")
      return
    }
    this.confirmDialog
      .open({
        title: "Delete station clock",
        message: "Are you sure you want to delete this station clock?",
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.stationClockService.deleteStationClock(id).subscribe({
          next: () => {
            this.showSnackBar("Station clock deleted successfully", "success")
            this.loadStationClocks()
          },
          error: (error) => {
            console.error("Error deleting station clock:", error)
            this.showSnackBar("Error deleting station clock", "error")
          },
        })
      })
  }

  getDayName(dayOfWeek: number): string {
    return dayOfWeekLabel(dayOfWeek)
  }

  getStationName(stationId: number): string {
    const station = this.stations.find((s) => s.id === stationId)
    return station ? station.call_sign : `Station ${stationId}`
  }

  formatTime(timeString: string): string {
    if (!timeString) return ""

    const [hours, minutes] = timeString.split(":")
    const hour = Number.parseInt(hours)
    const ampm = hour >= 12 ? "PM" : "AM"
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  private createNewClocks(formValue: any, selectedTimes: { day: number; time: string }[]) {
    const minute = Number(formValue.break_start_minute || 0)
    const minuteStr = minute.toString().padStart(2, "0")

    const breaks = selectedTimes.map((st) => {
      // st.time is "HH:00", we need just the HH part
      const [hour] = st.time.split(":")
      return {
        stationid: Number(formValue.stationid),
        break_length: Number(formValue.break_length),
        day_of_week: st.day,
        break_start_time: `${hour}:${minuteStr}:00`,
      }
    })

    this.stationClockService.createStationClocks(breaks).subscribe({
      next: () => {
        this.showSnackBar(
          `Station clock${selectedTimes.length > 1 ? "s" : ""} created successfully`,
          "success",
        )
        this.dialogSubmitting = false
        this.closeDialog()
        this.loadStationClocks()
      },
      error: (error) => {
        console.error("Error creating station clocks:", error)
        this.showSnackBar("Error creating station clocks", "error")
        this.dialogSubmitting = false
      },
    })
  }

  private showSnackBar(message: string, type: "success" | "error" | "warning") {
    const duration = 3000
    if (type === "success") this.snackbar.success(message, duration)
    else if (type === "warning") this.snackbar.warning(message, duration)
    else this.snackbar.error(message, duration)
  }

  goBack() {
    this.router.navigate(["/userhome"])
  }

  // Pagination methods
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page
      this.loadStationClocks()
    }
  }

  getPaginationPages(): number[] {
    const pages: number[] = []
    const maxPagesToShow = 5
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2))
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1)

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return pages
  }
}
